/**
 * OnlinePresencePanel — compact live-online strip.
 *
 * Renders a small horizontal card in the Home page hero showing the
 * avatars of users currently connected to the Hub WebSocket. Data is
 * SERVER-AUTHORITATIVE (`/api/hub/presence/online`) — the frontend
 * cannot spoof presence. When no one is online the panel shows a small
 * dimmed "no prophets online" state so it never looks broken.
 *
 * The panel refreshes every 30 seconds. That's enough to feel live
 * (users see their friends arriving/leaving inside a minute) without
 * hammering the endpoint from every open Hub tab.
 */
import { useEffect, useRef, useState } from "react";
import { hub } from "../../pages/HubPage";

const MAX_AVATARS = 6;

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Resolve a Discord avatar URL to something the browser can render.
 * Discord CDN URLs are already absolute — return verbatim. The
 * backend-served placeholder (`/api/hub/avatar-placeholder/240`) is
 * relative — prefix REACT_APP_BACKEND_URL so it works from any Hub
 * page and matches how the rest of the app resolves backend assets.
 */
function resolveAvatarUrl(url) {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("/")) return `${BACKEND}${trimmed}`;
  return trimmed;
}

/**
 * Two-letter initials for the fallback avatar tile (used when the
 * Discord URL is missing or 404s). Falls back to a single-letter "P"
 * so we never render an empty circle.
 */
function initials(name) {
  const s = (name || "").trim();
  if (!s) return "P";
  const parts = s.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

export function OnlinePresencePanel({ initial } = {}) {
  // `initial` is passed by HubHomePage after prehydration:
  //   • undefined → cap fired before we resolved; render the small
  //     "…" loading state and fetch normally.
  //   • object    → { count, users } — seed and skip the initial GET.
  //     The 30 s polling loop still runs so avatars stay live.
  const seeded = initial !== undefined;
  const [state, setState] = useState({
    loading: !seeded,
    count: seeded ? (initial.count || 0) : 0,
    users: seeded ? (initial.users || []) : [],
  });
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await hub.get("/hub/presence/online");
        if (cancelled) return;
        setState({ loading: false, count: data.count || 0, users: data.users || [] });
      } catch {
        if (cancelled) return;
        setState({ loading: false, count: 0, users: [] });
      }
    }
    // Skip the immediate fetch when the parent has prehydrated us —
    // the polling loop below still keeps us live.
    if (!seeded) load();
    timerRef.current = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [seeded]);

  const visible = state.users.slice(0, MAX_AVATARS);
  const overflow = Math.max(0, state.count - visible.length);

  return (
    <div className="hh-presence" data-testid="hh-presence">
      <div className="hh-presence-label">
        <span className="hh-presence-dot" aria-hidden="true" />
        <span className="hh-presence-text">ONLINE PROPHETS</span>
        {state.count > 0 && (
          <span className="hh-presence-count" data-testid="hh-presence-count">
            {state.count}
          </span>
        )}
      </div>
      <div className="hh-presence-avatars" data-testid="hh-presence-avatars">
        {state.loading ? (
          <span className="hh-presence-empty">…</span>
        ) : visible.length === 0 ? (
          <span className="hh-presence-empty" data-testid="hh-presence-empty">
            No prophets online
          </span>
        ) : (
          <>
            {visible.map(u => {
              const src = resolveAvatarUrl(u.avatar_url);
              return (
                <div key={u.user_id}
                     className="hh-presence-avatar"
                     title={`@${u.username || u.display_name}`}
                     data-testid={`hh-presence-avatar-${u.public_profile_id}`}>
                  {/* Fallback tile behind the img — becomes visible if the
                      Discord CDN URL 404s (avatar rotated, disabled, or
                      the user never uploaded one). Preserves the existing
                      generic-Prophet aesthetic without leaving a blank
                      circle. */}
                  <div className="hh-presence-avatar-fallback"
                       aria-hidden="true">
                    {initials(u.display_name || u.username)}
                  </div>
                  {src && (
                    <img src={src}
                         alt=""
                         loading="lazy"
                         referrerPolicy="no-referrer"
                         onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  )}
                  <span className="hh-presence-avatar-dot" aria-hidden="true" />
                </div>
              );
            })}
            {overflow > 0 && (
              <div className="hh-presence-more" data-testid="hh-presence-more">
                +{overflow}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
